import type { BsbBlock, BsbVerse } from './bsb-types';
import { tokenizeForGlossary, type PerBookOccurrences, type PerVerseMap } from './glossary';

export type ProjectedGlossarySegment =
  | { kind: 'plain'; text: string }
  | { kind: 'glow'; text: string; entryId: string; phraseText: string };

/**
 * Match once over the complete displayed verse, then project each match back
 * into its original text parts. Inline markup never adds a word boundary;
 * paragraph/poetry continuations contribute one space for matching only.
 * Those joining spaces are never inserted into the rendered Scripture.
 *
 * The builder's extractVerseText uses the same joining contract. Keeping the
 * original part boundaries lets the reader retain its paragraph, poetry and
 * words-of-Jesus elements while longest-phrase precedence applies verse-wide.
 */
export function projectGlossaryFragments(
  fragments: readonly (readonly string[])[],
  allowed: PerVerseMap,
): ProjectedGlossarySegment[][][] {
  const text = fragments.map((parts) => parts.join('')).join(' ');
  let cursor = 0;
  const spans = tokenizeForGlossary(text, allowed).map((segment) => {
    const start = cursor;
    cursor += segment.text.length;
    return { segment, start, end: cursor };
  });
  let offset = 0;
  return fragments.map((parts, fragmentIndex) => {
    if (fragmentIndex > 0) offset++;
    return parts.map((part) => {
      const start = offset;
      offset += part.length;
      const projected: ProjectedGlossarySegment[] = [];
      for (const span of spans) {
        const from = Math.max(start, span.start);
        const to = Math.min(offset, span.end);
        if (from >= to) continue;
        const piece = part.slice(from - start, to - start);
        projected.push(span.segment.kind === 'plain'
          ? { kind: 'plain', text: piece }
          : {
            kind: 'glow', text: piece, entryId: span.segment.entryId,
            phraseText: span.segment.text,
          });
      }
      return projected;
    });
  });
}

/** Original BsbVerse object identity maps every continuation to its own pieces. */
export function projectChapterGlossary(
  blocks: readonly BsbBlock[],
  occurrences: PerBookOccurrences,
  chapter: number,
): Map<BsbVerse, ProjectedGlossarySegment[][]> {
  const verses = new Map<number, BsbVerse[]>();
  for (const block of blocks) {
    const fragments = block.type === 'paragraph' ? block.verses
      : block.type === 'poetry' ? block.lines.flatMap((line) => line.verses) : [];
    for (const verse of fragments) {
      const group = verses.get(verse.v) ?? [];
      group.push(verse);
      verses.set(verse.v, group);
    }
  }
  const result = new Map<BsbVerse, ProjectedGlossarySegment[][]>();
  for (const [verseNumber, fragments] of verses) {
    const allowed = occurrences.get(`${chapter}:${verseNumber}`);
    if (!allowed?.size) continue;
    const projected = projectGlossaryFragments(fragments.map((verse) =>
      typeof verse.t === 'string' ? [verse.t] : verse.t.map((part) => part.text)), allowed);
    fragments.forEach((verse, index) => result.set(verse, projected[index]));
  }
  return result;
}
